import { type FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import { ToolNavigationLinkPrimary } from './ToolNavigationLinkPrimary';
import { ChildNavigationItem } from '../types';
import { moduleIconsMap } from '../../../const/moduleIconsMap';
import { ToolNavigationCollapseButton } from './ToolNavigationCollapseButton';
import { Collapse } from '../../../components/Collapse';
import { ToolNavigationLink } from './ToolNavigationLink';
import { useToolNavigationActiveModuleProvider } from './ToolNavigationActiveModuleProvider';
import { getCollapseButtonDataTestId, getNavItemDataTestId, getPrimaryLinkDataTestId, urlToTestId } from '../utils';
import classes from '../styles/index.module.css';
import { syncManager } from '../../../utils/syncManager';

export type ToolNavigationItemProps = {
	title: string;
	url: string;
	moduleName: string;
	pageTypeId: number;
	childNavigationItems: ChildNavigationItem[];
};

export const ToolNavigationItem: FC<ToolNavigationItemProps> = ({
	title,
	url,
	moduleName,
	pageTypeId,
	childNavigationItems,
}) => {
	const activePageTypeId = useToolNavigationActiveModuleProvider();
	const isActive =
		activePageTypeId === pageTypeId || childNavigationItems.some((i) => i.pageTypeId === activePageTypeId);

	const [isExpanded, setIsExpanded] = useState(isActive);

	/**
	 * Automatically collapses current ToolNavigationItem when any other has been opened
	 * and expands current ToolNavigationItem when it is active
	 */
	useEffect(() => {
		setIsExpanded(isActive);
	}, [isActive]);

	const showChildItems = childNavigationItems.length >= 2;

	const navigationItemClasses = classNames(classes.toolNavigationItem, {
		[classes.toolNavigationItem_open]: isExpanded,
	});

	const handleNavigationLinkClick = () => {
		syncManager.emit('react', 'onToolNavigationLinkClick');
	};

	return (
		<div
			className={navigationItemClasses}
			data-testid={getNavItemDataTestId(moduleName)}
			aria-expanded={isExpanded}
		>
			<div className={classes.toolNavigationItem__primaryLinkContainer}>
				<ToolNavigationLinkPrimary
					isActive={isActive}
					url={url}
					title={title}
					dataTestId={getPrimaryLinkDataTestId(moduleName)}
					icon={moduleIconsMap[moduleName]}
					onClick={handleNavigationLinkClick}
					hasCollapseButton={showChildItems}
				/>
				{showChildItems && (
					<ToolNavigationCollapseButton
						isExpanded={isExpanded}
						onClick={() => setIsExpanded((prev) => !prev)}
						dataTestId={getCollapseButtonDataTestId(moduleName)}
					/>
				)}
			</div>
			{showChildItems && (
				<Collapse isOpened={isExpanded}>
					{childNavigationItems.map((item) => (
						<ToolNavigationLink
							{...item}
							key={item.pageTypeId}
							onClick={handleNavigationLinkClick}
							isActive={activePageTypeId === item.pageTypeId}
							dataTestId={urlToTestId(item.url)}
						/>
					))}
				</Collapse>
			)}
		</div>
	);
};

ToolNavigationItem.displayName = 'ToolNavigationItem';
